import React, { useEffect } from 'react';
import { Shield, Lock, CreditCard } from 'lucide-react';
import { bookingData } from '../mock';

export const Booking = () => {
  useEffect(() => {
    // Check if script already loaded
    const existingScript = document.querySelector('script[src="https://login.smoobu.com/js/Settings/BookingToolIframe.js"]');
    
    if (!existingScript) {
      // Load Smoobu booking widget script
      const script = document.createElement('script');
      script.src = 'https://login.smoobu.com/js/Settings/BookingToolIframe.js';
      script.async = true;
      script.onload = () => {
        if (window.BookingToolIframe) {
          window.BookingToolIframe.initialize({
            url: "https://login.smoobu.com/en/booking-tool/iframe/1682697",
            baseUrl: "https://login.smoobu.com",
            target: "#apartmentIframeAll"
          });
        }
      };
      document.body.appendChild(script);
    } else if (window.BookingToolIframe) {
      // Script already loaded, just initialize
      window.BookingToolIframe.initialize({
        url: "https://login.smoobu.com/en/booking-tool/iframe/1682697",
        baseUrl: "https://login.smoobu.com",
        target: "#apartmentIframeAll"
      });
    }
  }, []);

  return (
    <section id="booking" className="py-12 sm:py-16 md:py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header - Mobile Optimized */}
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-[#071936] mb-3 sm:mb-4">
              Reserve Your Stay
            </h2>
            <p className="text-base sm:text-lg text-[#6B7280] px-4 mb-6">
              Check real-time availability and book securely
            </p>
            
            {/* Pricing Table */}
            <div className="max-w-md mx-auto mb-8">
              <div className="bg-white border border-[rgba(7,25,54,0.08)] rounded-lg overflow-hidden shadow-sm">
                {/* Header */}
                <div className="bg-[#FBFAF7] px-6 py-4 border-b border-[rgba(7,25,54,0.08)]">
                  <h3 className="text-lg font-medium text-[#071936]">Pricing</h3>
                  <p className="text-sm text-[#6B7280] mt-1">Nightly rates</p>
                </div>
                
                {/* Pricing rows */}
                <div className="divide-y divide-[rgba(7,25,54,0.05)]">
                  <div className="flex justify-between items-center px-6 py-3">
                    <span className="text-[#071936]">Monday</span>
                    <span className="text-[#6B7280]">Closed</span>
                  </div>
                  <div className="flex justify-between items-center px-6 py-3">
                    <span className="text-[#071936]">Tuesday</span>
                    <span className="text-[#6B7280]">Closed</span>
                  </div>
                  <div className="flex justify-between items-center px-6 py-3">
                    <span className="text-[#071936]">Wednesday</span>
                    <span className="text-[#6B7280]">Closed</span>
                  </div>
                  <div className="flex justify-between items-center px-6 py-3 bg-[#FBFAF7]/30">
                    <span className="text-[#071936] font-medium">Thursday</span>
                    <span className="text-[#071936] font-semibold">€ {bookingData.pricing.thursday}</span>
                  </div>
                  <div className="flex justify-between items-center px-6 py-3 bg-[#FBFAF7]/30">
                    <span className="text-[#071936] font-medium">Friday</span>
                    <span className="text-[#071936] font-semibold">€ {bookingData.pricing.friday}</span>
                  </div>
                  <div className="flex justify-between items-center px-6 py-3 bg-[#FBFAF7]/30">
                    <span className="text-[#071936] font-medium">Saturday</span>
                    <span className="text-[#071936] font-semibold">€ {bookingData.pricing.saturday}</span>
                  </div>
                  <div className="flex justify-between items-center px-6 py-3 bg-[#FBFAF7]/30">
                    <span className="text-[#071936] font-medium">Sunday</span>
                    <span className="text-[#071936] font-semibold">€ {bookingData.pricing.sunday}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Social Proof - Minimal */}
            <div className="mt-6 flex flex-col items-center gap-3 px-4">
              <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                <span className="text-[#2AA7A1]">★</span>
                <span className="font-medium text-[#071936]">4.98 rating</span>
                <span>·</span>
                <span>Over 150 reviews on Airbnb</span>
              </div>
              <p className="text-sm text-[#6B7280] italic max-w-md text-center leading-relaxed">
                "We had such a lovely stay at this glamping pod. It was clean, cosy, and had everything we needed for a relaxing break. The hot tub was the highlight - amazing, private, and perfect for unwinding in the evening. It really made the stay feel special. The area was peaceful and the host was friendly and helpful throughout. We would definitely stay again!"
              </p>
            </div>
          </div>

          {/* Booking Note - 2 Night Max */}
          <div className="mb-6 max-w-md mx-auto">
            <div className="bg-[#FBFAF7] border border-[rgba(7,25,54,0.08)] p-4 sm:p-5 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 text-[#2AA7A1] mt-0.5">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-[#071936] leading-relaxed" style={{ lineHeight: '1.6' }}>
                    <strong className="font-medium">Booking policy:</strong> Maximum 2-night stays to ensure availability for all guests throughout the season.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Smoobu Booking Widget Container - Seamless blend */}
          <div className="bg-white p-4 sm:p-6 rounded-lg relative">
            <div id="apartmentIframeAll" className="min-h-[400px] w-full -m-2">
              {/* Smoobu widget will inject here - loading state hidden via CSS once iframe loads */}
            </div>
            
            {/* Subtle help icon - bottom right corner */}
            <div className="absolute bottom-4 right-4 group">
              <div className="w-6 h-6 bg-[#2AA7A1]/10 hover:bg-[#2AA7A1]/20 rounded-full flex items-center justify-center cursor-help transition-all">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2AA7A1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              {/* Tooltip - shows on hover */}
              <div className="absolute bottom-8 right-0 bg-[#071936] text-white text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                Calendar not updating? Refresh the page.
              </div>
            </div>
          </div>

          {/* Trust & Security Note */}
          <div className="mt-6 sm:mt-8 bg-white border border-[rgba(7,25,54,0.08)] rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-500">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
              <div className="flex items-center gap-3 group">
                <div className="w-10 h-10 bg-[#DDF4F1] rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110">
                  <Lock className="text-[#2AA7A1]" size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#071936]">Secure Booking</h3>
                  <p className="text-xs text-[#6B7280]">Payments processed via Stripe</p>
                </div>
              </div>
              <div className="flex items-center gap-3 group">
                <div className="w-10 h-10 bg-[#DDF4F1] rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110">
                  <CreditCard className="text-[#2AA7A1]" size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#071936]">Flexible Payments</h3>
                  <p className="text-xs text-[#6B7280]">Credit card or debit card</p>
                </div>
              </div>
            </div>
            
            {/* Policies & Deposit Info */}
            <div className="border-t border-[rgba(7,25,54,0.08)] pt-4 mt-4">
              <div className="grid sm:grid-cols-2 gap-3 text-xs text-[#6B7280]">
                <div>
                  <p className="font-medium text-[#071936] mb-2">Booking Policies</p>
                  <ul className="space-y-1">
                    <li>• Stay length: {bookingData.minNights}-{bookingData.maxNights} nights</li>
                    <li>• Closed: Monday - Wednesday</li>
                    <li>• Check-in: {bookingData.checkInTime}</li>
                    <li>• Check-out: {bookingData.checkOutTime}</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-[#071936] mb-2">Security Deposit</p>
                  <p className="leading-relaxed">
                    A refundable deposit of €{bookingData.deposit.amount} per person is requested {bookingData.deposit.timing}. 
                    This is {bookingData.deposit.returnPolicy}. Payment can be made via {bookingData.deposit.paymentMethods} if preferred.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
